import { useEffect, useState, type MouseEvent } from 'react';

export interface SketchPreviewProps {
  /** The sketch's stable route, e.g. `/sketches/<id>`. Also the anchor's
   *  `href` — clicking the preview opens the live document in a new tab. */
  path: string;
  /** Used only for the anchor/iframe's accessible names — never rendered. */
  title: string;
  /** Cache-busting key — pass the sketch's `updatedAt` so an in-place edit
   *  reloads the iframe instead of showing a stale cached document (same
   *  key the tool result's own `updatedAt` cache-busts with). */
  cacheKey: string;
  width: number;
  height: number;
  /**
   * FRONT-2744: when set (the chat card passes it while the Canvas holds this
   * sketch) a plain click is handed to it instead of following the new-tab
   * link, and the accessible name says "beside the chat". The anchor keeps its
   * real `href`, so a modifier click still opens a new tab. The gallery passes
   * nothing and is unchanged.
   */
  onOpen?: (e: MouseEvent<HTMLAnchorElement>) => void;
}

/**
 * FRONT-1501: the scaled-iframe sketch thumbnail, extracted out of the chat
 * tool card (`~/features/chat/sketch-card.tsx`, FRONT-1338) so the gallery
 * (FRONT-1501) can reuse the identical technique rather than a second,
 * possibly-drifting copy — a virtual 1280x800 desktop viewport CSS-scaled
 * down to `width`x`height`, so a document authored for a real screen renders
 * faithfully at thumbnail size instead of reflowing to a narrow mobile-like
 * layout the way a plain responsive iframe would.
 *
 * Pure presentation: no fetch, no chrome around it beyond the frame itself —
 * callers wrap this in whatever header/footer/title their own card needs.
 */
export function SketchPreview({ path, title, cacheKey, width, height, onOpen }: SketchPreviewProps) {
  const virtualWidth = 1280;
  const virtualHeight = Math.round((virtualWidth / width) * height);
  const scale = width / virtualWidth;

  const [loaded, setLoaded] = useState(false);
  const src = `${path}?v=${encodeURIComponent(cacheKey)}`;

  useEffect(() => {
    setLoaded(false);
  }, [src]);

  return (
    <a
      href={path}
      target="_blank"
      rel="noreferrer noopener"
      onClick={onOpen}
      aria-label={onOpen ? `Open sketch ${title} beside the chat` : `Open sketch ${title} in a new tab`}
      className="relative block overflow-hidden bg-white dark:bg-zinc-950 border border-border rounded-md shrink-0 hover:border-primary/50 transition-colors"
      style={{ width, height }}
    >
      {!loaded && (
        <span className="absolute inset-0 flex items-center justify-center text-[length:var(--text-2xs)] text-muted-foreground">
          Loading…
        </span>
      )}
      <iframe
        src={src}
        title={`Preview of sketch ${title}`}
        sandbox="allow-scripts"
        loading="lazy"
        onLoad={() => setLoaded(true)}
        style={{
          width: virtualWidth,
          height: virtualHeight,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          border: 0,
          pointerEvents: 'none',
          // Fade the document in once loaded instead of popping — the
          // transition is interruptible and property-specific.
          opacity: loaded ? 1 : 0,
          transitionProperty: 'opacity',
          transitionDuration: '200ms',
        }}
      />
    </a>
  );
}
